import { KeyValue } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { IAddressDto, IOutletDto, INumberLoginDto } from 'src/app/core/models';
import { CommonConstants } from '../../constant';
import { BearerToken } from 'src/app/core/models/bearer.token';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import {
  CommonService,
  OutletService,
  ToastService,
  TokenService,
} from 'src/app/core/services';

@Component({
  selector: 'ic-add-outlet',
  templateUrl: './add-outlet.component.html',
  styleUrls: ['./add-outlet.component.scss'],
})
export class AddOutletComponent implements OnInit {
  @Input() id: number = 0;
  @Input() profile: boolean;
  public isAdministrator = false;
  public isManager = false;
  public isOutlet = false;
  public client: BearerToken;
  public outletForm: FormGroup;
  public mobileListFormArray: FormArray;
  public formErrors: any = {};
  public timeZoneList: KeyValue<number, string>[];
  public outlet: IOutletDto;
  public stateList: KeyValue<number, string>[];
  public cityList: KeyValue<number, string>[];
  public country = CommonConstants.Country;
  public address: IAddressDto;
  public isProd = environment.production;

  constructor(
    private readonly fb: FormBuilder,
    private readonly outletService: OutletService,
    private readonly toastService: ToastService,
    private readonly commonService: CommonService,
    private readonly tokenService: TokenService,
    private readonly router: Router,
  ) {
    this.client = this.tokenService.getToken();
    this.isAdministrator =
      this.client.roles ===
      CommonConstants.Role[CommonConstants.Role.Administrator];
    this.isManager =
      this.client.roles === CommonConstants.Role[CommonConstants.Role.Manager];
    this.isOutlet =
      this.client.roles === CommonConstants.Role[CommonConstants.Role.Outlet];
  }

  ngOnInit(): void {
    this.getTimeZone();

    this.outletForm = this.fb.group({
      name: ['', [Validators.required]],
      ownerName: ['', [Validators.required]],
      description: [''],
      email: ['', [Validators.required, Validators.email]],
      merchantEmail: [''],
      mobile: ['', [Validators.required]],
      mobile2: [''],
      //mobile2: ['', Validators.pattern(/^\(\d{3}\) \d{3}-\d{4}$/)],
      address: ['', [Validators.required]],
      countryId: [this.country.Usa, [Validators.required]],
      stateId: [null, [Validators.required]],
      cityId: [null, [Validators.required]],
      zipCode: ['', [Validators.required]],
      outletCoordinates: ['', [Validators.required]],
      googleAddress: ['', [Validators.required]],
      timeZoneId: [null, [Validators.required]],
      mobileList: this.fb.array([]),
    });

    // This forces validation.
    Object.keys(this.outletForm.controls).forEach((key) => {
      this.outletForm.get(key).markAsTouched();
    });

    this.outletForm.valueChanges.subscribe((changes) => {
      Object.keys(changes).forEach((controlName) => {
        const control = this.outletForm.controls[controlName];
        this.formErrors[controlName] =
          (control.touched || control.dirty) && control.errors ? 'error' : null;
      });
    });

    if (this.id > 0) {
      this.getOutlet();
    } else {
      this.address = {} as IAddressDto;
      this.getStates();
      this.addNewOption();
    }

    if (!this.isAdministrator) {
      this.mobileListFormArray = this.outletForm.get('mobileList') as FormArray;
      this.mobileListFormArray.controls.forEach((x) => x.disable());
    }
  }

  get fc() {
    return this.outletForm.controls;
  }

  addNewOption(model?: INumberLoginDto) {
    if (this.isOutlet) {
      return;
    }
    this.mobileListFormArray = this.outletForm.get('mobileList') as FormArray;
    this.mobileListFormArray.push(this.createOptionItem(model));
  }

  createOptionItem(model: INumberLoginDto): FormGroup {
    if (!model) {
      model = {} as INumberLoginDto;
    }
    return this.fb.group({
      name: [model?.name, [Validators.required, Validators.minLength(1)]],
      mobile: [model?.mobile, [Validators.required, Validators.minLength(1)]],
    });
  }

  removeOption(index: number) {
    this.mobileListFormArray.removeAt(index);
  }

  onSave() {
    const model = {
      ...this.outletForm.getRawValue(),
    } as IOutletDto;

    model.id = this.id;
    model.restaurantId = this.client.restaurantId;
    model.mobile = model.mobile.replace(/\D/g, '');
    // if (
    //   model.mobile.toString().includes('(') ||
    //   model.mobile.toString().includes(')')
    // ) {
    //   model.mobile2 = model.mobile2.replace(/\D/g, '');
    // }

    // if (!model.mobileList || model.mobileList.length === 0) {
    //   this.toastService.showError('Please add at least one Mobile/Username');
    //   return;
    // }

    // let isValidUsername = true;
    // const integerRegex = /^-?\d+$/;
    // model.mobileList.forEach((x) => {
    //   if (x.toString().length !== 10 || !integerRegex.test(x.toString())) {
    //     isValidUsername = false;
    //     return;
    //   }
    // });
    // if (!isValidUsername) {
    //   this.toastService.showError('Invalid Mobile/Username');
    //   return;
    // }

    this.outletService.saveOutlet(model).subscribe(
      (data) => {
        if (data.isSuccess) {
          this.toastService.showSuccess('Updated Successfully!');
          if (this.id === 0) {
            this.onPrevious();
          }
        } else {
          this.toastService.showError(data.errors[0]);
        }
      },
      (err) => {
        this.toastService.showError(err);
      },
    );
  }

  getTimeZone() {
    this.commonService.getTimeZone().subscribe(
      (data) => {
        this.timeZoneList = data;
      },
      (err) => {
        this.toastService.showError(err);
      },
    );
  }

  getStates(id: number = 0) {
    this.stateList = [];
    this.cityList = [];
    if (id === 0) {
      this.outletForm.get('stateId').setValue(null);
      this.outletForm.get('cityId').setValue(null);
      id = this.outletForm.get('countryId').value;
    }
    if (!id) {
      return;
    }
    this.commonService.getState(id).subscribe(
      (data) => {
        this.stateList = data;
      },
      (err) => {
        this.toastService.showError(err);
      },
    );
  }

  getCities(id: number = 0) {
    this.cityList = [];
    if (id === 0) {
      this.outletForm.get('cityId').setValue(null);
      id = this.outletForm.get('stateId').value;
    }
    if (!id) {
      return;
    }
    this.commonService.getCity(id).subscribe(
      (data) => {
        this.cityList = data;
      },
      (err) => {
        this.toastService.showError(err);
      },
    );
  }

  googleAddressEvent(event: IAddressDto) {
    if (event) {
      const coordinates = `${event.latitude},${event.longitude}`;
      this.outletForm.get('googleAddress').patchValue(event.address);
      this.outletForm.get('outletCoordinates').patchValue(coordinates);
    } else {
      this.outletForm.get('googleAddress').patchValue(null);
      this.outletForm.get('outletCoordinates').patchValue(null);
    }
  }

  private getOutlet() {
    this.outletService.getOutlet(this.id).subscribe(
      (data) => {
        this.outlet = data;
        this.bindOutlet();
      },
      (err) => {
        this.toastService.showError(err);
      },
    );
  }

  private bindOutlet() {
    this.outletForm.patchValue({
      name: this.outlet.name,
      ownerName: this.outlet.ownerName,
      description: this.outlet.description,
      email: this.outlet.email,
      // merchantEmail: this.outlet.merchantEmail
      //   ? this.outlet.merchantEmail.split(',')
      //   : null,
      merchantEmail: this.outlet.merchantEmail,
      mobile: this.outlet.mobile,
      mobile2: this.outlet.mobile2,
      address: this.outlet.address,
      countryId: this.outlet.countryId,
      stateId: this.outlet.stateId,
      cityId: this.outlet.cityId,
      zipCode: this.outlet.zipCode,
      outletCoordinates: this.outlet.outletCoordinates,
      googleAddress: this.outlet.googleAddress,
      timeZoneId: this.outlet.timeZoneId,
      // mobileList: this.outlet.mobileList,
    });

    if (this.outlet.outletCoordinates) {
      const coordinates = this.outlet.outletCoordinates.split(',');
      this.address = {
        latitude: Number(coordinates[0]),
        longitude: Number(coordinates[1]),
        address: this.outlet.googleAddress,
      } as IAddressDto;
    }

    if (this.outlet.mobileList?.length > 0) {
      this.outlet.mobileList.forEach((x: INumberLoginDto) => {
        this.addNewOption(x);
      });
    } else {
      this.addNewOption();
    }

    if (
      this.client.roles !==
      CommonConstants.Role[CommonConstants.Role.Administrator]
    ) {
      this.outletForm.get('email').disable();
    }
    this.getStates(this.outlet.countryId);
    this.getCities(this.outlet.stateId);
  }

  onPrevious() {
    this.router.navigate(['home/outlet']);
  }
}
